import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../api/client';

export interface Factory { factory_id: number; name: string; }
export interface UserInfo  { email: string; role: string; }

interface FactoryContextValue {
  activeFactory: Factory | null;
  factories: Factory[];
  user: UserInfo | null;
  switchFactory: (factoryId: number) => Promise<void>;
  setFromLogin: (factories: Factory[], activeId: number, user: UserInfo) => void;
}

const FactoryContext = createContext<FactoryContextValue>({
  activeFactory: null,
  factories: [],
  user: null,
  switchFactory: async () => {},
  setFromLogin: () => {},
});

// Persist across page refreshes by keeping in localStorage
function loadStoredUser(): UserInfo | null {
  try { return JSON.parse(localStorage.getItem('so_user') || 'null'); } catch { return null; }
}
function loadStoredFactories(): { factories: Factory[]; activeId: number } | null {
  try { return JSON.parse(localStorage.getItem('so_factories') || 'null'); } catch { return null; }
}

export function FactoryProvider({ children }: { children: React.ReactNode }) {
  const stored = loadStoredFactories();
  const [factories, setFactories] = useState<Factory[]>(stored?.factories ?? []);
  const [activeFactory, setActiveFactory] = useState<Factory | null>(
    stored ? (stored.factories.find(f => f.factory_id === stored.activeId) ?? null) : null,
  );
  const [user, setUser] = useState<UserInfo | null>(loadStoredUser);

  const setFromLogin = useCallback((facs: Factory[], activeId: number, u: UserInfo) => {
    setFactories(facs);
    const active = facs.find(f => f.factory_id === activeId) ?? facs[0] ?? null;
    setActiveFactory(active);
    setUser(u);
    localStorage.setItem('so_factories', JSON.stringify({ factories: facs, activeId }));
    localStorage.setItem('so_user', JSON.stringify(u));
  }, []);

  const switchFactory = useCallback(async (factoryId: number) => {
    const { data } = await api.post('/factories/switch', { factory_id: factoryId });
    localStorage.setItem('token', data.token);
    const next = factories.find(f => f.factory_id === factoryId) ?? null;
    setActiveFactory(next);
    localStorage.setItem('so_factories', JSON.stringify({ factories, activeId: factoryId }));
  }, [factories]);

  return React.createElement(
    FactoryContext.Provider,
    { value: { activeFactory, factories, user, switchFactory, setFromLogin } },
    children,
  );
}

export function useFactory() { return useContext(FactoryContext); }
